import { parseMarkdown, serializeMarkdown } from "./markdown";
import type { MonthData } from "../types";

const DB_NAME = "mavelux-ledger";
const DB_VERSION = 1;
const STORE_NAME = "handles";
const HANDLE_KEY = "ledger-dir";

let cachedDirHandle: FileSystemDirectoryHandle | null = null;

function openDB(): Promise<IDBObjectStore> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE_NAME);
    req.onsuccess = () => resolve(req.result.transaction(STORE_NAME, "readwrite").objectStore(STORE_NAME));
    req.onerror = () => reject(req.error);
  });
}

async function loadHandle(): Promise<FileSystemDirectoryHandle | null> {
  try {
    const store = await openDB();
    return new Promise((resolve) => {
      const req = store.get(HANDLE_KEY);
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => resolve(null);
      store.transaction?.commit();
    });
  } catch {
    return null;
  }
}

async function saveHandle(handle: FileSystemDirectoryHandle): Promise<void> {
  const store = await openDB();
  return new Promise((resolve, reject) => {
    const req = store.put(handle, HANDLE_KEY);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
    store.transaction?.commit();
  });
}

async function verifyPermission(handle: FileSystemDirectoryHandle): Promise<boolean> {
  const opts: FileSystemHandlePermissionDescriptor = { mode: "readwrite" };
  if ((await handle.queryPermission(opts)) === "granted") return true;
  if ((await handle.requestPermission(opts)) === "granted") return true;
  return false;
}

export async function pickLedgerDir(): Promise<FileSystemDirectoryHandle> {
  const handle = await window.showDirectoryPicker();
  if (!(await verifyPermission(handle))) throw new Error("Permission denied to ledger directory");
  cachedDirHandle = handle;
  await saveHandle(handle);
  return handle;
}

export async function getLedgerDir(): Promise<FileSystemDirectoryHandle | null> {
  if (cachedDirHandle) return cachedDirHandle;
  const handle = await loadHandle();
  if (!handle) return null;
  if (!(await verifyPermission(handle))) {
    cachedDirHandle = null;
    return null;
  }
  cachedDirHandle = handle;
  return handle;
}

export async function hasLedgerDir(): Promise<boolean> {
  return (await getLedgerDir()) !== null;
}

export async function readMonth(month: string): Promise<MonthData> {
  const dir = await getLedgerDir();
  if (!dir) throw new Error("No ledger directory selected");
  const filename = `${month}.md`;
  try {
    const fileHandle = await dir.getFileHandle(filename);
    const file = await fileHandle.getFile();
    const content = await file.text();
    return parseMarkdown(content);
  } catch {
    return { raw_materials: [], packaging: [], finished_goods: [] };
  }
}

export async function writeMonth(month: string, data: MonthData): Promise<void> {
  const dir = await getLedgerDir();
  if (!dir) throw new Error("No ledger directory selected");
  const filename = `${month}.md`;
  const content = serializeMarkdown(month, data);
  const fileHandle = await dir.getFileHandle(filename, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(content);
  await writable.close();
}

export async function listMonths(): Promise<string[]> {
  const dir = await getLedgerDir();
  if (!dir) return [];
  const months: string[] = [];
  for await (const entry of dir.values()) {
    if (entry.kind === "file") {
      const name = entry.name;
      if (name.endsWith(".md") && name.length === 9) {
        const stem = name.slice(0, -3);
        if (/^\d{4}-\d{2}$/.test(stem)) {
          months.push(stem);
        }
      }
    }
  }
  months.sort((a, b) => b.localeCompare(a));
  return months;
}

export async function readLedgerFile(filename: string): Promise<string> {
  const dir = await getLedgerDir();
  if (!dir) throw new Error("No ledger directory selected");
  try {
    const fileHandle = await dir.getFileHandle(filename);
    const file = await fileHandle.getFile();
    return await file.text();
  } catch {
    return "";
  }
}

export async function writeLedgerFile(filename: string, content: string): Promise<void> {
  const dir = await getLedgerDir();
  if (!dir) throw new Error("No ledger directory selected");
  const fileHandle = await dir.getFileHandle(filename, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(content);
  await writable.close();
}
